import { Injectable, inject, signal, computed } from "@angular/core";
import {
  injectQuery,
  injectMutation,
  injectQueryClient,
  queryOptions,
} from "@ngneat/query";
import { derivedAsync } from "ngxtension/derived-async";
import { TenantUrlUtilService } from "@shared/services/tenant-url-util.service";
import { ServiceRescheduleDataService } from "../services/service-reschedule-data.service";
import {
  ServiceRescheduleRequest,
  ServiceRescheduleResponseData,
} from "../models/service-reschedule.model";
import { SERVICE_RESCHEDULE_UI_CONFIG } from "../constants/service-reschedule.constants";

@Injectable({ providedIn: "root" })
export class ServiceRescheduleState {
  private readonly serviceRescheduleDataService = inject(
    ServiceRescheduleDataService,
  );
  private readonly tenantUrlUtil = inject(TenantUrlUtilService);
  private readonly query = injectQuery();
  private readonly queryClient = injectQueryClient();
  private readonly mutation = injectMutation();

  readonly serviceId = signal<string | null>(null);
  readonly serviceItemId = signal<string | null>(null);

  readonly maxVisibleSchedules = signal(
    SERVICE_RESCHEDULE_UI_CONFIG.DEFAULT_VISIBLE_SCHEDULE_COUNT,
  );

  private getSchedulesQueryOptions(tenantId: string, serviceId: string) {
    return queryOptions({
      queryKey: ["service-schedules", tenantId, serviceId] as const,
      queryFn: () =>
        this.serviceRescheduleDataService.fetchAvailableSchedules(
          tenantId,
          serviceId,
        ),
    });
  }

  readonly schedulesQuery = derivedAsync(() => {
    const tenantId = this.tenantUrlUtil.normalizedTenant();
    const serviceId = this.serviceId();

    return this.query({
      ...this.getSchedulesQueryOptions(tenantId!, serviceId!),
      enabled: !!tenantId && !!serviceId,
    }).result$;
  });

  readonly isLoading = computed(() => {
    const query = this.schedulesQuery();
    return query?.isLoading ?? false;
  });

  readonly schedulesError = computed(() => {
    const query = this.schedulesQuery();
    return query?.error ?? null;
  });

  readonly hasError = computed(() => !!this.schedulesError());

  readonly availableSchedules = computed(() => {
    const query = this.schedulesQuery();
    const data = query?.data ?? [];

    return data.sort((a, b) => {
      const dateA = this.parseDate(a);
      const dateB = this.parseDate(b);
      return dateA.valueOf() - dateB.valueOf();
    });
  });

  readonly visibleSchedules = computed(() => {
    const schedules = this.availableSchedules();
    const max = this.maxVisibleSchedules();
    return schedules.slice(0, max);
  });

  readonly hasMoreSchedules = computed(
    () =>
      this.availableSchedules().length >
      SERVICE_RESCHEDULE_UI_CONFIG.DEFAULT_VISIBLE_SCHEDULE_COUNT,
  );

  readonly isShowingAllSchedules = computed(
    () => this.maxVisibleSchedules() >= this.availableSchedules().length,
  );

  readonly isLoaded = computed(
    () =>
      !this.isLoading() &&
      !this.hasError() &&
      this.availableSchedules().length > 0,
  );

  readonly isAllowedToReschedule = computed(() => {
    const query = this.schedulesQuery();
    const isSuccess = query?.isSuccess ?? false;
    const hasError = this.hasError();
    const hasSchedules = this.availableSchedules().length > 0;
    return isSuccess && !hasError && hasSchedules;
  });

  readonly canReschedule = computed(
    () => this.isAllowedToReschedule() && !this.isLoading(),
  );

  readonly mutationError = signal<any>(null);

  readonly isBusinessRuleError = computed(() => {
    const error = this.mutationError();
    return error?.status === 409 || error?.code === 2;
  });

  readonly isGeneralMutationError = computed(() => {
    const error = this.mutationError();
    return !!error && error?.status !== 409 && error?.code !== 2;
  });

  readonly rescheduleServiceMutation = this.mutation({
    mutationFn: async (payload: {
      tenantId: string;
      request: ServiceRescheduleRequest;
    }) => {
      this.mutationError.set(null);
      const result = await this.serviceRescheduleDataService.rescheduleService(
        payload.tenantId,
        payload.request,
      );
      return result;
    },
    onSuccess: async (response: ServiceRescheduleResponseData) => {
      if (response.success) {
        //await sleep(2000);
        await this.queryClient.invalidateQueries({
          queryKey: ["order-detail"],
          exact: false,
        });
        await this.queryClient.invalidateQueries({
          queryKey: ["orders", "services"],
          exact: false,
        });

        await this.queryClient.invalidateQueries({
          queryKey: ["services"],
          exact: false,
        });
      }
    },
    onError: (error: any) => {
      this.mutationError.set(error);
    },
  });

  showMoreSchedules(): void {
    this.maxVisibleSchedules.set(this.availableSchedules().length);
  }

  showLessSchedules(): void {
    this.maxVisibleSchedules.set(
      SERVICE_RESCHEDULE_UI_CONFIG.DEFAULT_VISIBLE_SCHEDULE_COUNT,
    );
  }

  async refetchSchedules() {
    const tenantId = this.tenantUrlUtil.normalizedTenant();
    const serviceId = this.serviceId();

    if (!tenantId || !serviceId) return;

    await this.queryClient.refetchQueries({
      queryKey: ["service-schedules", tenantId, serviceId],
      exact: true,
    });
  }

  resetUIState(): void {
    this.maxVisibleSchedules.set(
      SERVICE_RESCHEDULE_UI_CONFIG.DEFAULT_VISIBLE_SCHEDULE_COUNT,
    );
  }

  resetMutationState(): void {
    this.mutationError.set(null);
    this.rescheduleServiceMutation.reset();
  }

  private parseDate(dateString: string): Date {
    const [day, month, year] = dateString.split("/");
    return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  }
}
